import { useState } from 'react'
import { Check, Shield, Lock } from 'lucide-react'
import { useI18n } from '../i18n'
import { profileAPI } from '../services/api'
import { formatDate } from '../utils/dateUtils'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * MedicareIdLookup — v238.9.0 (S17-5), i18n v238.11.0
 *
 * Resolves a patient's Medicare Beneficiary Identifier (MBI) from CMS via Stedi
 * when they don't have their Medicare card on hand. The SSN is entered into a
 * masked (password) field, sent over TLS for the lookup, and NEVER echoed back
 * or stored — on success the field is cleared and only the resolved MBI remains.
 *
 * Brand-locked colors only (navy / blue / green / slate). No purple, no amber.
 *
 * Props:
 *   userId      — patient user id
 *   onResolved  — (mbi: string) => void   called with the resolved MBI
 */
const NAVY = '#15354F'
const BLUE = '#46A8F9'
const GREEN = '#66D170'
const SLATE = '#94A3B8'

export default function MedicareIdLookup({ userId, onResolved }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [ssn, setSsn] = useState('')        // transient — never persisted/echoed
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(null) // {type:'success'|'pending'|'error', msg}

  const digits = ssn.replace(/\D/g, '')
  const canLookup = digits.length === 9 || digits.length === 4 || digits.length === 0

  const runLookup = async () => {
    if (!userId || loading) return
    setLoading(true); setStatus(null)
    try {
      const resp = await profileAPI.lookupMbi(userId, { ssn: digits || undefined })
      const r = resp?.mbi || {}
      // PROD-1131: the API answers HTTP 200 with a TOP-LEVEL error ('lookup_limit' | 'lookup_unavailable')
      //   and limitReason ('found' | 'attempts' | 'in_flight' | 'unavailable') beside mbi:{found:false}.
      //   'found' is the stored MBI (r.found above); the others must not read as "no match".
      const limited = resp?.error === 'lookup_limit' ? resp?.limitReason : null
      // PROD-1164 F1: month 'long', never 'short'. With 'short', JavaScriptCore (Safari, the iOS WebView)
      //   orders es-US month-first and es "después del {{date}}" reads "después del oct 25"; 'long' is
      //   day-first in every engine ("después del 25 de octubre"), as in eligibilityLimit.js.
      const nextDate = limited === 'attempts' ? formatDate(resp?.nextCheckAt, { month: 'long', day: 'numeric' }) : ''
      if (r.found && r.mbi) {
        setSsn('')  // wipe the SSN from memory the moment we're done with it
        setStatus({ type: 'success', msg: t('cob.mbiFound', 'Medicare ID found and saved.') })
        if (onResolved) onResolved(r.mbi)
      } else if (r.enrollment_pending) {
        setStatus({ type: 'pending', msg: t('cob.mbiPending', "We're activating Medicare lookups for your clinician's office — try again shortly.") })
      } else if (limited === 'in_flight') {
        setStatus({ type: 'pending', msg: t('cob.mbiPending', "We're activating Medicare lookups for your clinician's office — try again shortly.") })
      } else if (limited === 'attempts' && nextDate) {
        setStatus({ type: 'error', msg: t('cob.mbiLimit', { defaultValue: "You've reached the limit for Medicare ID lookups. Try again after {{date}}.", date: nextDate }) })
      } else if (limited === 'attempts' || limited === 'unavailable' || resp?.error === 'lookup_unavailable') {
        setStatus({ type: 'error', msg: t('cob.mbiFailed', 'Lookup failed. Please try again.') })
      } else {
        setStatus({ type: 'error', msg: t('cob.mbiNotFound', "Couldn't find a match. Double-check your name, date of birth, and SSN.") })
      }
    } catch (e) {
      setStatus({ type: 'error', msg: t('cob.mbiFailed', 'Lookup failed. Please try again.') })
    } finally {
      setLoading(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full py-2.5 border-[1.5px] border-dashed rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2 mb-4"
        style={{ borderColor: 'rgba(70,168,249,0.3)', color: BLUE }}
      >
        <Shield size={15} /> {t('cob.findMedicareCta', "Don't have your Medicare ID? Find it")}
      </button>
    )
  }

  return (
    <div className="rounded-[14px] p-4 mb-4 border-[1.5px]" style={{ borderColor: 'rgba(70,168,249,0.25)', background: 'rgba(233,248,255,0.5)' }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Lock size={15} style={{ color: BLUE }} />
        <h4 className="text-[13px] font-bold" style={{ color: NAVY }}>{t('cob.findMedicareTitle', 'Find my Medicare ID')}</h4>
      </div>
      <p className="text-[11px] mb-3" style={{ color: SLATE }}>
        {t('cob.findMedicareDesc', 'We look up your Medicare Beneficiary ID with CMS using your name, date of birth, and SSN. Your SSN is used only for this check — it is never stored.')}
      </p>

      <label className="block text-[12px] mb-1" style={{ color: '#6b7785' }}>{t('cob.ssnLabel', 'Social Security Number')}</label>
      <input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        value={ssn}
        onChange={(e) => setSsn(e.target.value.replace(/[^\d-]/g, '').slice(0, 11))}
        placeholder="•••-••-••••"
        className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors mb-1 tracking-widest"
        style={{ color: NAVY }}
      />
      <p className="text-[10px] mb-3" style={{ color: SLATE }}>{t('cob.ssnHint', 'No SSN on hand? Leave blank to try a name + date-of-birth lookup.')}</p>

      {status && (
        <div className="flex items-start gap-2 mb-3 text-[12px] font-medium"
          style={{ color: status.type === 'success' ? GREEN : status.type === 'pending' ? BLUE : SLATE }}>
          {status.type === 'success' && <Check size={15} className="flex-shrink-0 mt-0.5" />}
          <span>{status.msg}</span>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={runLookup}
          disabled={loading || !canLookup}
          className="flex-1 h-10 rounded-full text-[13px] font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ background: BLUE }}
        >
          {loading ? <><OrellaSpinner size={16} tone="inherit" /> {t('cob.lookingUp', 'Looking up')}</> : t('cob.findMyMedicareId', 'Find my Medicare ID')}
        </button>
        <button
          onClick={() => { setSsn(''); setOpen(false); setStatus(null) }}
          className="px-4 h-10 rounded-full text-[13px] font-semibold"
          style={{ background: '#fff', color: NAVY, border: '1.5px solid #D8E6F0' }}
        >
          {t('cob.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  )
}
